import React, { useEffect, useMemo, useState } from "react";
import api from "../../services/api";
import PlansManagementView from "./PlansManagementView";
import {
  PLAN_ACCESS_OPTIONS,
  mapPlanTypesToAccessOptions,
  normalizePlan,
} from "./plansManagement.utils";
import "./PlansManagement.css";

const PlansManagement = () => {
  const [plans, setPlans] = useState([]);
  const [planAccessOptions, setPlanAccessOptions] = useState(PLAN_ACCESS_OPTIONS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const [searchQuery, setSearchQuery] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [activeFilter, setActiveFilter] = useState("");
  const [displayFilter, setDisplayFilter] = useState("");
  const [sortKey, setSortKey] = useState("id");
  const [sortDir, setSortDir] = useState("asc");
  const [currentPage, setCurrentPage] = useState(1);
  const [modalMode, setModalMode] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);

  const itemsPerPage = 8;

  const loadPlans = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await api.get("/plans-new");
      setPlans((response?.result || []).map(normalizePlan));
    } catch (err) {
      setError(err?.message || "Failed to load plans");
      setPlans([]);
    } finally {
      setLoading(false);
    }
  };

  const loadPlanAccessOptions = async () => {
    try {
      const response = await api.get("/pro-plan-types");
      const planTypes = response?.data || [];
      const options = mapPlanTypesToAccessOptions(planTypes);
      if (options.length > 0) {
        setPlanAccessOptions(options);
      }
    } catch (err) {
      console.warn("Failed to load pro plan types for plan access:", err?.message || err);
    }
  };

  useEffect(() => {
    loadPlans();
    loadPlanAccessOptions();
  }, []);

  const filteredPlans = useMemo(() => {
    const query = appliedSearch.trim().toLowerCase();

    const filtered = plans.filter((plan) => {
      const access = String(plan.plan_access_id || []).toLowerCase();
      const matchesSearch =
        !query ||
        plan.name.toLowerCase().includes(query) ||
        String(plan.tip_message || "").toLowerCase().includes(query) ||
        String(plan.razorpay_plan || "").toLowerCase().includes(query) ||
        access.includes(query);

      const matchesType = !typeFilter || plan.plan_type === typeFilter;
      const matchesActive = !activeFilter || plan.active_status === activeFilter;
      const matchesDisplay = !displayFilter || plan.display === displayFilter;

      return matchesSearch && matchesType && matchesActive && matchesDisplay;
    });

    return filtered.sort((a, b) => {
      const left = a[sortKey];
      const right = b[sortKey];

      if (["id", "price_full", "price_discounted"].includes(sortKey)) {
        return sortDir === "asc"
          ? Number(left || 0) - Number(right || 0)
          : Number(right || 0) - Number(left || 0);
      }

      const leftValue = Array.isArray(left) ? left.join(",") : String(left ?? "");
      const rightValue = Array.isArray(right) ? right.join(",") : String(right ?? "");
      return sortDir === "asc"
        ? leftValue.localeCompare(rightValue)
        : rightValue.localeCompare(leftValue);
    });
  }, [
    plans,
    appliedSearch,
    typeFilter,
    activeFilter,
    displayFilter,
    sortKey,
    sortDir,
  ]);

  const totalPages = Math.max(1, Math.ceil(filteredPlans.length / itemsPerPage));
  const paginatedPlans = filteredPlans.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [typeFilter, activeFilter, displayFilter, sortKey, sortDir, appliedSearch]);

  const summary = useMemo(() => {
    const total = plans.length;
    const active = plans.filter((plan) => plan.active_status === "Yes").length;
    const visible = plans.filter((plan) => plan.display === "Yes").length;
    const accessTypes = new Set();

    plans.forEach((plan) => {
      const ids = Array.isArray(plan.plan_access_id) ? plan.plan_access_id : [];
      ids.forEach((id) => accessTypes.add(String(id)));
    });

    return {
      total,
      active,
      visible,
      accessTypes: accessTypes.size,
    };
  }, [plans]);

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
      return;
    }
    setSortKey(key);
    setSortDir("asc");
  };

  const closeModal = () => {
    setModalMode(null);
    setSelectedPlan(null);
  };

  const handleSave = async (formData) => {
    setSaving(true);
    setError("");
    try {
      const payload = {
        ...formData,
        name: String(formData.name || "").trim(),
        price_full: formData.price_full === "" ? null : Number(formData.price_full),
        price_discounted: formData.price_discounted === "" ? null : Number(formData.price_discounted),
      };

      if (formData.id) {
        await api.put(`/plans-new/${formData.id}`, payload);
      } else {
        await api.post("/plans-new", payload);
      }

      await loadPlans();
      closeModal();
    } catch (err) {
      setError(err?.message || "Failed to save plan");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    setSaving(true);
    setError("");
    try {
      await api.delete(`/plans-new/${id}`);
      await loadPlans();
      closeModal();
    } catch (err) {
      setError(err?.message || "Failed to delete plan");
    } finally {
      setSaving(false);
    }
  };

  return (
    <PlansManagementView
      plans={plans}
      filteredCount={filteredPlans.length}
      loading={loading}
      error={error}
      searchQuery={searchQuery}
      onSearchChange={setSearchQuery}
      onSearchApply={() => setAppliedSearch(searchQuery)}
      typeFilter={typeFilter}
      onTypeFilterChange={setTypeFilter}
      activeFilter={activeFilter}
      onActiveFilterChange={setActiveFilter}
      displayFilter={displayFilter}
      onDisplayFilterChange={setDisplayFilter}
      sortKey={sortKey}
      sortDir={sortDir}
      onSort={handleSort}
      currentPage={currentPage}
      onPageChange={setCurrentPage}
      totalPages={totalPages}
      paginatedPlans={paginatedPlans}
      summary={summary}
      planAccessOptions={planAccessOptions}
      onAdd={() => {
        setSelectedPlan(null);
        setModalMode("add");
      }}
      onEdit={(plan) => {
        setSelectedPlan(plan);
        setModalMode("edit");
      }}
      onView={(plan) => {
        setSelectedPlan(plan);
        setModalMode("view");
      }}
      onDelete={(plan) => {
        setSelectedPlan(plan);
        setModalMode("delete");
      }}
      modalMode={modalMode}
      selectedPlan={selectedPlan}
      onCloseModal={closeModal}
      onSavePlan={handleSave}
      onDeletePlan={handleDelete}
      saving={saving}
    />
  );
};

export default PlansManagement;
